import { Link } from 'react-router-dom'

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <title>Privacy Policy - Start Grounded</title>
      <meta name="description" content="Privacy policy for Start Grounded AI trainer app" />

      <div className="max-w-4xl mx-auto px-4 py-12 safe-top">
        <Link to="/" className="text-blue-600 hover:text-blue-800 mb-8 inline-block">
          ← Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-6 text-gray-700">
          <p className="text-sm text-gray-500">Last updated: February 19, 2026</p>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
            <p className="mb-4">Start Grounded collects the following types of information to provide you with personalized AI trainer services:</p>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Information</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Phone Number:</strong> Used for account authentication and workout reminders via SMS and voice calls</li>
              <li><strong>Name:</strong> To personalize your experience with your AI trainer</li>
              <li><strong>Availability Preferences:</strong> Your preferred workout times and days</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">Workout Data</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Exercise History:</strong> Sets, reps, weights, and completion status</li>
              <li><strong>Progress Photos:</strong> Optional photos you upload to track your fitness journey</li>
              <li><strong>Session Logs:</strong> Dates, times, and details of completed workouts</li>
              <li><strong>Workout Preferences:</strong> Exercise selections, difficulty levels, and program goals</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">Communication Data</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>SMS Messages:</strong> Workout reminders and motivational messages from your AI trainer</li>
              <li><strong>Call Logs:</strong> Records of voice call reminders (if enabled)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <p className="mb-2">We use your information to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Generate personalized workout programs tailored to your goals</li>
              <li>Send automated workout reminders via SMS and voice calls</li>
              <li>Track your progress and adjust training intensity</li>
              <li>Provide accountability through automated check-ins</li>
              <li>Improve our AI training algorithms</li>
              <li>Ensure account security and prevent fraud</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Data Storage and Security</h2>
            <p className="mb-4">Your data is stored securely using:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Firebase Firestore:</strong> Encrypted cloud database for workout data and user profiles</li>
              <li><strong>Firebase Storage:</strong> Secure cloud storage for progress photos</li>
              <li><strong>Industry-Standard Encryption:</strong> All data is encrypted in transit and at rest</li>
              <li><strong>Access Controls:</strong> Only you and authorized system processes can access your data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Third-Party Services</h2>
            <p className="mb-4">We use the following third-party services:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Twilio:</strong> SMS and voice call delivery for workout reminders</li>
              <li><strong>OpenAI:</strong> AI-powered workout generation and text-to-speech services</li>
              <li><strong>Firebase (Google):</strong> Authentication, database, and cloud storage</li>
            </ul>
            <p className="mt-4">These services have their own privacy policies and we ensure they meet strict data protection standards.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Sharing</h2>
            <p className="mb-4">We do NOT sell your personal data to third parties. We only share your data:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>With service providers necessary to deliver our services (Twilio, OpenAI, Firebase)</li>
              <li>When required by law or to protect our legal rights</li>
              <li>With your explicit consent for specific purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
            <p className="mb-4">You have the right to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and all associated data</li>
              <li><strong>Opt-Out:</strong> Disable SMS/call reminders while keeping your account active</li>
              <li><strong>Data Portability:</strong> Export your workout data in a machine-readable format</li>
            </ul>
            <p className="mt-4">To exercise these rights, contact us at <a href="mailto:privacy@startgrounded.app" className="text-blue-600 hover:underline">privacy@startgrounded.app</a></p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Retention</h2>
            <p>We retain your data for as long as your account is active. If you delete your account:</p>
            <ul className="list-disc ml-6 space-y-2 mt-2">
              <li>Workout data and progress photos are permanently deleted within 30 days</li>
              <li>Phone number and authentication data are removed immediately</li>
              <li>Anonymized usage statistics may be retained for service improvement</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
            <p>Start Grounded is not intended for users under 18 years old. We do not knowingly collect data from children.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Changes to This Policy</h2>
            <p>We may update this privacy policy from time to time. We will notify you of significant changes via SMS or email. Continued use of our service after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="mb-2">If you have questions about this privacy policy or your data, contact us:</p>
            <ul className="space-y-1">
              <li><strong>Email:</strong> <a href="mailto:privacy@startgrounded.app" className="text-blue-600 hover:underline">privacy@startgrounded.app</a></li>
              <li><strong>Support:</strong> <a href="mailto:support@startgrounded.app" className="text-blue-600 hover:underline">support@startgrounded.app</a></li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  )
}
